<template>
    <div>
        <FilterToggle>
            <input type="hidden" v-model="filters.query" />
            <FormControlSector
                name="sectors"
                v-model="filters.sectors"
                :multiple="true"
                :include-ancestors="false"
            />
            <FormControlDealType
                name="deal_type"
                v-model="filters.deal_type"
            />
            <FormControlMember
                name="member"
                v-model="filters.member"
                placeholder="Select..."
                label="Member company"
            />
            <FormControlDatepicker
                name="fromDate"
                v-model="filters.fromDate"
                label="From date"
            />
            <FormControlDatepicker
                name="toDate"
                v-model="filters.toDate"
                label="To date"
            />
            <div class="form__group download">
                <a :href="downloadURL" :disabled="$wait.is('loading_search_madds')" class="btn btn-download">Download All</a>
            </div>
        </FilterToggle>
        <MADDTableHeader
            @changeSort="handleSortChange"
        />
        <div v-if="$wait.is('loading_search_madds')" class="loading">
            <Spinner :height="40" :width="40"/>
        </div>
        <div v-else-if="items && items.length > 0">
            <ul class="search-results">
                <li v-for="item in items">
                    <MADDSearchResult :resource="item" />
                </li>
            </ul>
            <Pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
            />
        </div>
        <div v-else class="no-results">
            No results
        </div>
    </div>
</template>
<script>
import debounce from 'debounce';
import API from '@api';
import FormControlSector from '@components/Form/Control/FormControlSector';
import FormControlDealType from '@components/Form/Control/FormControlDealType';
import FormControlMember from '@components/Form/Control/FormControlMember';
import FormControlDatepicker from '@components/Form/Control/FormControlDatepicker';
import Spinner from '@components/Spinner';
import Pagination from '@components/List/Pagination';
import MADDTableHeader from '@components/Search/Headers/MADD';
import MADDSearchResult from '@components/Search/Result/MADD';
import qs from 'qs';
import FilterToggle from '@components/Search/FilterToggle.vue';
import OpportunityTableHeader from '@components/Search/Headers/Opportunity.vue';

export default {
    name: 'SearchPageMADDs',
    components: {
        OpportunityTableHeader,
        FilterToggle,
        MADDSearchResult,
        MADDTableHeader,
        Pagination,
        FormControlDatepicker,
        Spinner,
        FormControlMember,
        FormControlDealType,
        FormControlSector,
    },
    props: {
        query: {
            type: String,
            default: '',
        },
        enableSynonyms: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            filters: {
                query: this.query,
                sectors: [],
                deal_type: null,
                member: null,
                fromDate: null,
                toDate: null,
                sort: null,
                enable_synonyms: this.enableSynonyms
            },
            items: [],
            paginationMeta: {
                page: 1,
            },
        }
    },
    methods: {
        handleChange: () => {},
        handleSortChange(newSortValue) {
            this.filters.sort = `completed_at:${newSortValue}`;
        },
        async fetchResource() {
            if(this.$wait.is('loading_search_madds'))
                return;

            try {
                this.$wait.start('loading_search_madds');

                const {
                    data,
                    meta,
                } = await API.searchMADDs({
                    ...this.filters,
                    page: this.paginationMeta.page,
                });

                this.items = data;
                this.paginationMeta = meta;
            } catch(err) {
                throw new Error(err.message);
            } finally {
                this.$wait.end('loading_search_madds');
            }
        },
        load() {
            this.fetchResource();
        },
        onChangePage(page) {
            this.paginationMeta = {
                ...this.paginationMeta,
                page
            }

            this.fetchResource();
        },
    },
    computed: {
        downloadURL() {
            return '/madds/download?' + qs.stringify(this.filters);
        }
    },
    watch: {
        'filters': {
            handler: function() {
                this.onChangePage(1);
            },
            deep: true,
        },
        query: {
            handler: function(newQuery) {
                this.filters.query = newQuery;
            },
            deep: true,
        },
        enableSynonyms: {
            handler: function(newValue) {
                this.filters.enable_synonyms = newValue;
            },
            deep: true,
        },
        paginationMeta: {
            handler: function() {
                setTimeout(() => { // Timing issue with $refs being inside a v-if
                    if(this.$refs.pagination) {
                        this.$refs.pagination.setPaginationData(this.paginationMeta);
                    }
                })
            },
            deep: true,
        },
    },
    created() {
        this.handleChange = debounce(
            this.fetchResource.bind(this),
            800,
        );
    },
}
</script>
<style lang="scss" scoped>
.toolbar__filters {
    @media screen and (max-width: 1200px) {
        .flex-fix {
            flex-wrap: wrap;
            gap: 1rem;
            width: 100%;

            .form__group {
                flex: 0 0 calc(33% - .6rem);

                + .form__group {
                    margin-left: 0;
                }
            }
        }
    }

    @media screen and (max-width: 991px) {
        border-bottom: 1px solid #000;
        padding-bottom: 2rem;

        .flex-fix {
            flex-direction: row;

            .form__group {
                width: calc(33% - .6rem);
            }
        }
    }

    @media screen and (max-width: 575px) {
        .flex-fix {
            .form__group {
                flex: 0 0 calc(50% - .6rem);
                width: calc(50% - .6rem);
            }
        }
    }
}
</style>
<style lang="scss">
@import "~@/theme/_variables.scss";

.search-results {
    list-style: none;
    padding: 0;
    margin: 0 auto 5rem;

    li + li {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: .1rem solid $border;
    }
}

.toolbar .toolbar__filters .form__group {
    width: 16.3rem;
}

.download {
    .btn {
        margin-top: 2.1rem;
    }
}

@media screen and (max-width: 1310px) {
    .toolbar .toolbar__filters .form__group {
        & + .form__group {
            margin-left: 1rem;
        }

        width: 15.5rem;
    }
}

@media screen and (max-width: 991px) {
    .download {
        .btn-download {
            display: initial;
        }
    }
}
</style>
